import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserModel, UserRoleEnum } from '../interfaces/user.interface';
import {
  autoFetchUserDetails,
  fetchTrainingVideos,
  fetchUserDetails,
  logOut,
  signInStudent,
  signInUser,
} from './thunks/user.thunk';
import { RootState, store } from './store';
import { UserData } from '../pages/Builder/components/SignUp/SignUpData.interface';
import {
  getTeacherClassrooms,
  getTeacherClassroomsByAdmin,
  setProjectAsDefaultByAdmin,
} from './thunks/user-teacher.thunk';
import { ClassroomModel } from '../interfaces/classroom.interface';
import { TeacherOnboardingModel, TeacherTourPayload } from '../interfaces/teacher.interface';
import { ProjectModel } from '../interfaces/project.interface';
import { LocationType } from '../pages/Auth/LocationSelection';
import { UserOnboardingModel } from '../interfaces/user-onboarding.interface';

// Define the user state interface
interface UserState {
  userUpdateSubscriptionAndUnsubscribe: (() => void) | null;
  unsubscribeUserSnapShot: (() => void) | null;
  user: UserModel | null;
  userAuthorized: boolean | null;
  showLogin: boolean;
  stopLoadUpdates: boolean;
  isLoading: boolean;
  isUserLoading: boolean;
  showRecreateUserSignup: boolean;
  userPlanSubscribed: boolean;
  isSending: boolean;
  signupViaGoogle: UserData | null;
  singleBookPlan: boolean;
  showTrainingPopup: Record<string, boolean>;
  trainingVideos: Record<string, string>;
  redirectURL: string | null;

  classrooms: ClassroomModel[] | null;
  limitClassroomStudents: number;
  activeClassroom: ClassroomModel | null;
  activeClassroomProject: ProjectModel | null;
  activeClassroomProjects: ProjectModel[] | null;
  teacherOnboarding: TeacherOnboardingModel | null;
  userOnboarding: UserOnboardingModel | null;
  teacherTour: TeacherTourPayload | null;
  showStartTourPopup: boolean;
  showPublishingTimelinePopup: { projectId?: string; openAddStudentsPopup?: boolean } | null;
  showAddNewProject: boolean;
  selectProjectById: string | null;
  showAddNewStudentsPopup: boolean | null;
  isNewClassroomFlow: boolean;
  isOnboarding: boolean;
  studentPreOrderBook: Record<string, string> | null;

  studentLocationAuth: LocationType | null;
  studentTokenAuth: string | null;
}

const initialState: UserState = {
  userUpdateSubscriptionAndUnsubscribe: null,
  unsubscribeUserSnapShot: null,
  user: null,
  userAuthorized: null,
  showLogin: true,
  stopLoadUpdates: false,
  isLoading: true,
  isUserLoading: true,
  showRecreateUserSignup: false,
  userPlanSubscribed: false,
  isSending: false,
  signupViaGoogle: null,
  singleBookPlan: false,
  showTrainingPopup: {
    screen1: true,
    screen2: true,
    screen3: true,
    screen4: true,
  },
  trainingVideos: {},
  redirectURL: null,

  classrooms: null,
  limitClassroomStudents: 0,
  activeClassroom: null,
  activeClassroomProject: null,
  activeClassroomProjects: null,
  teacherOnboarding: null,
  userOnboarding: null,
  teacherTour: null,
  showStartTourPopup: false,
  showPublishingTimelinePopup: null,
  showAddNewProject: false,
  selectProjectById: null,
  showAddNewStudentsPopup: null,
  isNewClassroomFlow: false,
  isOnboarding: false,
  studentPreOrderBook: null,

  studentLocationAuth: null,
  studentTokenAuth: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserUpdateSubscriptionAndUnsubscribe(state, action: PayloadAction<(() => void) | null>) {
      state.userUpdateSubscriptionAndUnsubscribe = action.payload;
    },
    setUnsubscribeUserSnapShot(state, action: PayloadAction<(() => void) | null>) {
      state.unsubscribeUserSnapShot = action.payload;
    },
    setUser(state, action: PayloadAction<any | null>) {
      state.user = action.payload || null;
      state.userAuthorized = !!action.payload;
    },
    setShowLogin(state, action: PayloadAction<boolean>) {
      state.showLogin = action.payload;
    },
    setSignupViaGoogle(state, action: PayloadAction<boolean | null | any>) {
      state.signupViaGoogle = action.payload;
    },
    setSingleBookPlan(state, action: PayloadAction<boolean>) {
      state.singleBookPlan = action.payload;
    },
    setShowTrainingPopup(state, action: PayloadAction<Record<string, boolean>>) {
      state.showTrainingPopup = action.payload;
    },
    setUserLoader(state, action: PayloadAction<boolean>) {
      state.isUserLoading = action.payload;
    },
    setStopLoadUpdates(state, action: PayloadAction<boolean>) {
      state.stopLoadUpdates = action.payload;
    },
    setUserPlanSubscribed(state, action: PayloadAction<boolean>) {
      state.userPlanSubscribed = action.payload;
    },
    setRedirectURL(state, action: PayloadAction<string>) {
      state.redirectURL = action.payload;
    },
    setShowRecreateUserSignup(state, action: PayloadAction<{ show: boolean }>) {
      state.showRecreateUserSignup = action.payload?.show;
    },
    setActiveClassroom(state, action: PayloadAction<{ classroom: ClassroomModel | null }>) {
      state.activeClassroom = action.payload?.classroom || null;
    },
    setActiveClassroomProject(state, action: PayloadAction<{ project: ProjectModel | null }>) {
      state.activeClassroomProject = action.payload?.project || null;
    },
    setActiveClassroomProjects(state, action: PayloadAction<{ projects: any[] | null }>) {
      state.activeClassroomProjects = action.payload?.projects || null;
    },
    setTeacherOnboarding(state, action: PayloadAction<{ data: Partial<TeacherOnboardingModel> }>) {
      state.teacherOnboarding = { ...(state.teacherOnboarding || {}), ...action.payload?.data };
    },
    setUserOnboarding(state, action: PayloadAction<{ data: Partial<UserOnboardingModel> }>) {
      state.userOnboarding = { ...(state.userOnboarding || {}), ...action.payload?.data };
    },
    setTeacherTour(state, action: PayloadAction<TeacherTourPayload>) {
      state.teacherTour = { ...(state.teacherTour || {}), ...action.payload };
    },
    setNewClassroomFlow(state, action: PayloadAction<boolean>) {
      state.isNewClassroomFlow = action.payload;
    },
    setIsOnboarding(state, action: PayloadAction<boolean>) {
      state.isOnboarding = action.payload;
    },
    setShowPublishingTimelinePopup(
      state,
      action: PayloadAction<{ projectId?: string; openAddStudentsPopup?: boolean } | null>,
    ) {
      state.showPublishingTimelinePopup = action.payload;
    },
    setShowAddNewProject(state, action: PayloadAction<boolean>) {
      state.showAddNewProject = action.payload;
    },
    setSelectProjectById(state, action: PayloadAction<string | null>) {
      state.selectProjectById = action.payload;
    },
    setShowAddNewStudentsPopup(state, action: PayloadAction<boolean | null>) {
      state.showAddNewStudentsPopup = action.payload;
    },
    setStudentLocationAuth(state, action: PayloadAction<LocationType | null>) {
      state.studentLocationAuth = action.payload;
    },
    setStudentTokenAuth(state, action: PayloadAction<string | null>) {
      state.studentTokenAuth = action.payload;
    },
    setStudentPreOrderBook(state, action: PayloadAction<Record<string, string> | null>) {
      state.studentPreOrderBook = action.payload;
    },
    setLimitClassroomStudents(state, action: PayloadAction<number>) {
      state.limitClassroomStudents = action.payload;
    },
    setShowStartTourPopup(state, action: PayloadAction<boolean>) {
      state.showStartTourPopup = action.payload;
    },
    setSelectedProjectInClassrooms(state, action: PayloadAction<{ classroomId: string; projectId: string }>) {
      state.classrooms = [
        ...(state.classrooms || []).map((classroom) => {
          if (classroom.id !== action.payload?.classroomId) {
            return classroom;
          }
          const activeProject = (classroom.projects || []).find((project) => project.id === action.payload.projectId);
          return {
            ...classroom,
            // we don't need to set activeProjectId here (it defined from backend side)
            // activeProjectId: action.payload.projectId,
            activeProject: activeProject ? { ...activeProject } : undefined,
          };
        }),
      ];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User
      .addCase(fetchUserDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action: PayloadAction<any | null>) => {
        state.user = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchUserDetails.rejected, (state) => {
        state.user = null;
        state.isLoading = false;
      })

      // Sign in user with email
      .addCase(signInUser.pending, (state) => {
        state.isSending = true;
      })
      .addCase(signInUser.fulfilled, (state, action: PayloadAction<any | null>) => {
        state.isSending = false;
      })
      .addCase(signInUser.rejected, (state) => {
        state.isSending = false;
      })

      // Sign in user with student username
      .addCase(signInStudent.pending, (state) => {
        state.isSending = true;
      })
      .addCase(signInStudent.fulfilled, (state, action: PayloadAction<any | null>) => {
        state.isSending = false;
      })
      .addCase(signInStudent.rejected, (state) => {
        state.isSending = false;
      })

      // Fetch Training Videos
      .addCase(fetchTrainingVideos.fulfilled, (state, action: PayloadAction<Record<string, string>>) => {
        state.trainingVideos = action.payload;
      })

      .addCase(autoFetchUserDetails.pending, (state) => {})
      .addCase(autoFetchUserDetails.fulfilled, (state, action: PayloadAction<any | null>) => {
        state.user = action.payload;
        state.isUserLoading = false;
      })
      .addCase(autoFetchUserDetails.rejected, (state) => {
        state.user = null;
        state.isUserLoading = false;
      })

      .addCase(logOut.pending, (state) => {
        state.isUserLoading = true;
      })
      .addCase(logOut.fulfilled, (state) => {
        state.isUserLoading = false;
      })
      .addCase(logOut.rejected, (state) => {
        state.isUserLoading = false;
      })

      .addCase(getTeacherClassrooms.fulfilled, (state, action: PayloadAction<ClassroomModel[] | null>) => {
        state.classrooms = action.payload ?? null;
      })
      .addCase(getTeacherClassrooms.rejected, (state) => {
        state.classrooms = null;
      })
      .addCase(getTeacherClassroomsByAdmin.pending, (state) => {
        state.classrooms = null;
      })
      .addCase(getTeacherClassroomsByAdmin.fulfilled, (state, action: PayloadAction<ClassroomModel[] | null>) => {
        state.classrooms = action.payload ?? null;
      })
      .addCase(getTeacherClassroomsByAdmin.rejected, (state) => {
        state.classrooms = null;
      })
      .addCase(setProjectAsDefaultByAdmin.fulfilled, (state, action) => {
        if (!action.payload || !state.classrooms) {
          return;
        }

        const { classroomId, projectId } = action.payload;

        state.classrooms = state.classrooms.map((classroom) => {
          if (classroom.id !== classroomId) {
            return classroom;
          }
          const activeProject = (classroom.projects || []).find((project) => project.id === projectId);
          return {
            ...classroom,
            activeProjectId: projectId,
            activeProject: activeProject ? { ...activeProject } : undefined,
          };
        });
      });
  },
});

export const {
  setUserUpdateSubscriptionAndUnsubscribe,
  setUnsubscribeUserSnapShot,
  setUser,
  setShowLogin,
  setSignupViaGoogle,
  setSingleBookPlan,
  setShowTrainingPopup,
  setUserLoader,
  setStopLoadUpdates,
  setUserPlanSubscribed,
  setRedirectURL,
  setShowRecreateUserSignup,
  setActiveClassroom,
  setActiveClassroomProject,
  setActiveClassroomProjects,
  setTeacherOnboarding,
  setUserOnboarding,
  setTeacherTour,
  setShowStartTourPopup,
  setNewClassroomFlow,
  setIsOnboarding,
  setShowPublishingTimelinePopup,
  setShowAddNewProject,
  setSelectProjectById,
  setShowAddNewStudentsPopup,
  setStudentLocationAuth,
  setStudentTokenAuth,
  setStudentPreOrderBook,
  setLimitClassroomStudents,
  setSelectedProjectInClassrooms,
} = userSlice.actions;

export const selectUser = (state: RootState) => state.user;
export const selectIsSending = (state: RootState) => state.user.isSending;
export const selectSingleBookPlan = (state: RootState) => state.user.singleBookPlan;

export const userStore = () => {
  const dataStore = selectUser(store.getState());

  return { ...dataStore };
};

export const isAdmin = () => {
  const user = selectUser(store.getState()).user;
  return !!user && [UserRoleEnum.admin, UserRoleEnum.superAdmin, UserRoleEnum.customerService].includes(user?.role!);
};

export default userSlice.reducer;
